import React, { useState } from "react";
import { FaMapMarkerAlt, FaTimes } from "react-icons/fa";
import countries from "../../countries.json";
import { saveTempBuyerLocation, describeBuyerLocation, isValidBuyerLocation } from "../../utils/buyerLocation";
import { useLocationContext } from "../../utils/locationContext";
import "./LocationFilterBar.css";

// Consumer Target Location + search radius -- rendered inside the Elakay
// Filters panel (Filter_fitlerP.js), under the Portrait/Landscape toggle.
// Reuses the exact same buyerLocation param shape everywhere else in the app
// (utils/buyerLocation.js): saving it here is what every product / shop /
// Product Preview request sends, and what the server's Location Context
// (server_elakay elakay_services/locationContext.js) filters on. This bar
// never hides cards on its own, it only changes what gets requested.
// saveTempBuyerLocation() dispatches BUYER_LOCATION_EVENT, which is how
// ProductGridPage.jsx (the owner of the state driving product queries) and
// utils/locationContext.js stay in sync.
//
// Without a target, the bar shows the destination the SERVER resolved (the
// default address, or an approximate location from the connection) so the
// consumer knows what the results are based on.
const RADIUS_OPTIONS = [5, 10, 25, 50, 100, 250];

const LocationFilterBar = ({ infoSource, location, onChange, hasDefaultAddress, onUseDefaultAddress, initiallyEditing = false }) => {
  const tr = (key, fallback) =>
    infoSource?.laguangeElakay?.("localSelling", infoSource.languageAPI)?.[key] || fallback;

  const serverContext = useLocationContext();
  const [editing, setEditing] = useState(initiallyEditing);
  const [country, setCountry] = useState(location?.country || serverContext.location?.country || "");
  const [city, setCity] = useState(location?.city || "");
  const [state, setState] = useState(location?.state || "");
  const [postalCode, setPostalCode] = useState(location?.postalCode || "");
  const [radius, setRadius] = useState(location?.radius ? String(location.radius) : "");

  const applyChange = (next) => {
    saveTempBuyerLocation(next);
    onChange?.(next);
    setEditing(false);
  };

  const handleApply = () => {
    const next = { country: country.trim(), city: city.trim(), state: state.trim(), postalCode: postalCode.trim() };
    if (!isValidBuyerLocation(next)) return;
    if (Number(radius) > 0) Object.assign(next, { radius: Number(radius), radiusUnit: "mi" });
    applyChange(next);
  };

  const handleReset = () => {
    setCity(""); setState(""); setPostalCode(""); setRadius("");
    saveTempBuyerLocation(null);
    onChange?.(null);
    setEditing(false);
  };

  const label = describeBuyerLocation(location);
  const resolved = serverContext.location;
  const resolvedLabel = resolved?.label || "";
  const resolvedHint = resolved?.source === "ip"
    ? tr("approximateLocation", "approximate, based on your connection")
    : resolved?.source === "default_address" || resolved?.source === "selected_address"
      ? tr("fromYourAddress", "your address")
      : "";

  let summary;
  if (label) summary = tr("nearLabel", "Products available near") + " " + label;
  else if (resolvedLabel) summary = `${tr("nearLabel", "Products available near")} ${resolvedLabel}${resolvedHint ? ` (${resolvedHint})` : ""}`;
  else summary = tr("noLocation", "Select a location to see local products");

  return (
    <div className="local-selling-filter-bar">
      <span className="local-selling-filter-summary" data-testid="location-filter-summary">
        <FaMapMarkerAlt />
        {summary}
      </span>

      <div className="local-selling-filter-actions">
        {hasDefaultAddress && label && (
          <button type="button" onClick={onUseDefaultAddress}>
            {tr("useDefaultAddress", "Use my default address")}
          </button>
        )}
        <button type="button" onClick={() => setEditing((v) => !v)}>
          {tr("changeLocation", "Change location")}
        </button>
        {label && (
          <button type="button" onClick={handleReset} aria-label={tr("reset", "Reset")}>
            <FaTimes />
          </button>
        )}
      </div>

      {editing && (
        <div className="local-selling-filter-form">
          <select aria-label={tr("modeCountry", "Country")} value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">{tr("modeCountry", "Country")}</option>
            {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          <input aria-label={tr("modeCity", "City")} placeholder={tr("modeCity", "City")} value={city} onChange={(e) => setCity(e.target.value)} />
          <input aria-label={tr("modeState", "State / Province")} placeholder={tr("modeState", "State / Province")} value={state} onChange={(e) => setState(e.target.value)} />
          <input aria-label={tr("modeZip", "ZIP / Postal code")} placeholder={tr("modeZip", "ZIP / Postal code")} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
          <select aria-label={tr("searchRadius", "Search radius")} value={radius} onChange={(e) => setRadius(e.target.value)}>
            <option value="">{tr("anyDistance", "Any distance")}</option>
            {RADIUS_OPTIONS.map((r) => <option key={r} value={r}>{`${tr("within", "Within")} ${r} mi`}</option>)}
          </select>
          <button type="button" onClick={handleApply}>{tr("search", "Search")}</button>
        </div>
      )}
    </div>
  );
};

export default LocationFilterBar;
